import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import workerProfileService from '../../../services/workerProfileService';

const WorkerProfilesManagement = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    rating: 4.5,
    reviews: 0,
    location: '',
    specialization: '',
    certifications: '',
    experience: '',
    availability: '',
    hourlyRate: '',
    completedProjects: 0,
    warranty: '',
    serviceArea: 'Zonnepanelen',
    isActive: true
  });
  const [errors, setErrors] = useState({});
  const [filterServiceArea, setFilterServiceArea] = useState('all');

  const serviceAreas = [
    'Zonnepanelen',
    'Elektrische Installatie',
    'HVAC Systemen',
    'Loodgieters',
    'Kraan Operators',
    'Stratenmakers & Grondwerkers'
  ];

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    try {
      setLoading(true);
      const data = await workerProfileService?.getAllWorkerProfiles();
      setProfiles(data || []);
    } catch (error) {
      console.error('Error loading worker profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (profile = null) => {
    if (profile) {
      setEditingProfile(profile);
      setFormData({
        name: profile?.name,
        rating: profile?.rating,
        reviews: profile?.reviews,
        location: profile?.location,
        specialization: profile?.specialization,
        certifications: profile?.certifications?.join(', ') || '',
        experience: profile?.experience,
        availability: profile?.availability,
        hourlyRate: profile?.hourly_rate,
        completedProjects: profile?.completed_projects,
        warranty: profile?.warranty,
        serviceArea: profile?.service_area,
        isActive: profile?.is_active
      });
    } else {
      setEditingProfile(null);
      setFormData({
        name: '',
        rating: 4.5,
        reviews: 0,
        location: '',
        specialization: '',
        certifications: '',
        experience: '',
        availability: '',
        hourlyRate: '',
        completedProjects: 0,
        warranty: '',
        serviceArea: 'Zonnepanelen',
        isActive: true
      });
    }
    setErrors({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProfile(null);
    setFormData({
      name: '',
      rating: 4.5,
      reviews: 0,
      location: '',
      specialization: '',
      certifications: '',
      experience: '',
      availability: '',
      hourlyRate: '',
      completedProjects: 0,
      warranty: '',
      serviceArea: 'Zonnepanelen',
      isActive: true
    });
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.name?.trim()) {
      newErrors.name = 'Naam is verplicht';
    }
    if (!formData?.location?.trim()) {
      newErrors.location = 'Locatie is verplicht';
    }
    if (!formData?.specialization?.trim()) {
      newErrors.specialization = 'Specialisatie is verplicht';
    }
    if (!formData?.serviceArea) {
      newErrors.serviceArea = 'Vakgebied is verplicht';
    }
    if (formData?.rating < 0 || formData?.rating > 5) {
      newErrors.rating = 'Rating moet tussen 0 en 5 zijn';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    try {
      const profileData = {
        ...formData,
        certifications: formData?.certifications?.split(',')?.map(c => c?.trim())?.filter(c => c)
      };

      if (editingProfile) {
        await workerProfileService?.updateWorkerProfile(editingProfile?.id, profileData);
      } else {
        // Check if service area already has 9 profiles
        const count = await workerProfileService?.countWorkerProfilesByServiceArea(formData?.serviceArea);
        if (count >= 9) {
          setErrors({ submit: `Maximum van 9 profielen bereikt voor ${formData?.serviceArea}` });
          return;
        }
        await workerProfileService?.createWorkerProfile(profileData);
      }
      await loadProfiles();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving worker profile:', error);
      setErrors({ submit: error?.message });
    }
  };

  const handleDelete = async (profileId) => {
    if (!window.confirm('Weet je zeker dat je dit werknemersprofiel wilt verwijderen?')) return;

    try {
      await workerProfileService?.deleteWorkerProfile(profileId);
      await loadProfiles();
    } catch (error) {
      console.error('Error deleting worker profile:', error);
      alert('Fout bij verwijderen: ' + error?.message);
    }
  };

  const filteredProfiles = filterServiceArea === 'all' 
    ? profiles 
    : profiles?.filter(p => p?.service_area === filterServiceArea);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Werknemersprofielen Beheer</h2>
          <p className="text-sm text-muted-foreground">Beheer beschikbare werknemers per vakgebied (max 9 per gebied)</p>
        </div>
        <Button onClick={() => handleOpenModal()} iconName="Plus">
          Profiel Toevoegen
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <label className="text-sm font-medium text-foreground">Filter op vakgebied:</label>
        <select
          value={filterServiceArea}
          onChange={(e) => setFilterServiceArea(e?.target?.value)}
          className="px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">Alle vakgebieden</option>
          {serviceAreas?.map(area => (
            <option key={area} value={area}>{area}</option>
          ))}
        </select>
        <span className="text-sm text-muted-foreground">
          ({filteredProfiles?.length} profielen)
        </span>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Laden...</div>
      ) : (
        <div 
          className="bg-white border-2 border-slate-200 rounded-lg overflow-hidden"
          style={{
            boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
          }}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Naam</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Vakgebied</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Locatie</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Rating</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Uurtarief</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Status</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-foreground">Acties</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredProfiles?.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">
                      Geen werknemersprofielen gevonden
                    </td>
                  </tr>
                ) : (
                  filteredProfiles?.map((profile) => (
                    <tr key={profile?.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3 text-sm text-foreground font-medium">{profile?.name}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{profile?.service_area}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{profile?.location}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Icon name="Star" size={14} className="text-warning fill-warning" />
                          {profile?.rating}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{profile?.hourly_rate}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          profile?.is_active ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                        }`}>
                          {profile?.is_active ? 'Actief' : 'Inactief'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Edit"
                          onClick={() => handleOpenModal(profile)}
                        >
                          Bewerken
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Trash2"
                          onClick={() => handleDelete(profile?.id)}
                        >
                          Verwijderen
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-1030 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={handleCloseModal} />
          <div 
            className="relative bg-white border-2 border-slate-200 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            style={{
              boxShadow: '0 20px 60px rgba(0,0,0,0.2), 0 10px 30px rgba(0,0,0,0.12)'
            }}
          >
            <div className="px-6 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold text-foreground">
                {editingProfile ? 'Werknemersprofiel Bewerken' : 'Nieuw Werknemersprofiel'}
              </h3>
              <Button variant="ghost" size="icon" onClick={handleCloseModal}>
                <Icon name="X" size={20} />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Naam"
                  type="text"
                  placeholder="Bijv. SolarTech Professionals"
                  value={formData?.name}
                  onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                  error={errors?.name}
                  required
                />

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Vakgebied *
                  </label>
                  <select
                    value={formData?.serviceArea}
                    onChange={(e) => setFormData({ ...formData, serviceArea: e?.target?.value })}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  >
                    {serviceAreas?.map(area => (
                      <option key={area} value={area}>{area}</option>
                    ))}
                  </select>
                  {errors?.serviceArea && (
                    <p className="text-sm text-error mt-1">{errors?.serviceArea}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Locatie"
                  type="text"
                  placeholder="Bijv. Amsterdam"
                  value={formData?.location}
                  onChange={(e) => setFormData({ ...formData, location: e?.target?.value })}
                  error={errors?.location}
                  required
                />

                <Input
                  label="Specialisatie"
                  type="text"
                  placeholder="Bijv. Residentiële Installaties"
                  value={formData?.specialization}
                  onChange={(e) => setFormData({ ...formData, specialization: e?.target?.value })}
                  error={errors?.specialization}
                  required
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <Input
                  label="Rating (0-5)"
                  type="number"
                  step="0.1"
                  min="0"
                  max="5"
                  placeholder="4.5"
                  value={formData?.rating}
                  onChange={(e) => setFormData({ ...formData, rating: parseFloat(e?.target?.value) })}
                  error={errors?.rating}
                />

                <Input
                  label="Aantal Reviews"
                  type="number"
                  placeholder="0"
                  value={formData?.reviews}
                  onChange={(e) => setFormData({ ...formData, reviews: parseInt(e?.target?.value) || 0 })}
                />

                <Input
                  label="Voltooide Projecten"
                  type="number"
                  placeholder="0"
                  value={formData?.completedProjects}
                  onChange={(e) => setFormData({ ...formData, completedProjects: parseInt(e?.target?.value) || 0 })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Certificeringen (gescheiden door komma's)
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Bijv. NEN 3140, VCA, ISO 9001"
                  value={formData?.certifications}
                  onChange={(e) => setFormData({ ...formData, certifications: e?.target?.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Ervaring"
                  type="text"
                  placeholder="Bijv. 15+ jaar"
                  value={formData?.experience}
                  onChange={(e) => setFormData({ ...formData, experience: e?.target?.value })}
                />

                <Input
                  label="Beschikbaarheid"
                  type="text"
                  placeholder="Bijv. Direct beschikbaar"
                  value={formData?.availability}
                  onChange={(e) => setFormData({ ...formData, availability: e?.target?.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Uurtarief"
                  type="text"
                  placeholder="Bijv. €75"
                  value={formData?.hourlyRate}
                  onChange={(e) => setFormData({ ...formData, hourlyRate: e?.target?.value })}
                />

                <Input
                  label="Garantie"
                  type="text"
                  placeholder="Bijv. 5 jaar garantie"
                  value={formData?.warranty}
                  onChange={(e) => setFormData({ ...formData, warranty: e?.target?.value })}
                />
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_active"
                  checked={formData?.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
                <label htmlFor="is_active" className="text-sm text-foreground cursor-pointer">
                  Actief
                </label>
              </div>

              {errors?.submit && (
                <div className="text-sm text-error">{errors?.submit}</div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={handleCloseModal}>
                  Annuleren
                </Button>
                <Button type="submit">
                  {editingProfile ? 'Opslaan' : 'Toevoegen'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkerProfilesManagement;